import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Shield, BarChart3, Megaphone, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "cb_cookie_consent";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const writeConsent = (prefs) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...prefs, essential: true, timestamp: new Date().toISOString() })
  );
  // Signal analytics/ads scripts to (de)activate. Real tags read this on load.
  window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: prefs }));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setAnalytics(!!existing.analytics);
      setAdvertising(!!existing.advertising);
    }
    const openManager = () => {
      const cur = readConsent();
      if (cur) {
        setAnalytics(!!cur.analytics);
        setAdvertising(!!cur.advertising);
      }
      setManagerOpen(true);
    };
    window.addEventListener("open-cookie-preferences", openManager);
    return () => window.removeEventListener("open-cookie-preferences", openManager);
  }, []);

  const acceptAll = useCallback(() => {
    writeConsent({ analytics: true, advertising: true });
    setVisible(false);
    setManagerOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    writeConsent({ analytics: false, advertising: false });
    setVisible(false);
    setManagerOpen(false);
  }, []);

  const savePreferences = useCallback(() => {
    writeConsent({ analytics, advertising });
    setVisible(false);
    setManagerOpen(false);
  }, [analytics, advertising]);

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="cookie-banner"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6"
          >
            <div className="glass-strong mx-auto flex max-w-5xl flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <div className="flex items-start gap-3">
                <Cookie className="mt-0.5 h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
                <p className="text-sm leading-relaxed text-zinc-300">
                  We use cookies to enhance your experience, analyze traffic, and serve personalized ads
                  (including Google AdSense &amp; Analytics). See our{" "}
                  <Link to="/cookies" className="text-gold underline-offset-2 hover:underline">Cookie Policy</Link> and{" "}
                  <Link to="/privacy" className="text-gold underline-offset-2 hover:underline">Privacy Policy</Link>.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button data-testid="cookie-manage" onClick={() => setManagerOpen(true)}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-zinc-200 transition-colors hover:bg-white/5">
                  Manage Preferences
                </button>
                <button data-testid="cookie-reject" onClick={rejectNonEssential}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-zinc-200 transition-colors hover:bg-white/5">
                  Reject Non-Essential
                </button>
                <button data-testid="cookie-accept" onClick={acceptAll}
                  className="rounded-full bg-gradient-to-r from-nebula to-gold px-4 py-2 text-xs font-semibold text-white transition-transform hover:scale-105">
                  Accept All
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
        <DialogContent data-testid="cookie-manager" className="glass-strong border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Cookie Preferences</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Manage how we use cookies. Essential cookies are always active.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <PrefRow icon={Shield} title="Essential Cookies" desc="Required for the site to function. Always on." locked checked />
            <PrefRow icon={BarChart3} title="Analytics Cookies" desc="Help us understand traffic (Google Analytics)."
              checked={analytics} onChange={setAnalytics} testid="pref-analytics" />
            <PrefRow icon={Megaphone} title="Advertising Cookies" desc="Used to serve personalized ads (Google AdSense)."
              checked={advertising} onChange={setAdvertising} testid="pref-advertising" />
          </div>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button onClick={rejectNonEssential} className="rounded-full border border-white/15 px-4 py-2 text-xs text-zinc-200 hover:bg-white/5">Reject Non-Essential</button>
            <button data-testid="cookie-save" onClick={savePreferences} className="rounded-full bg-gradient-to-r from-nebula to-gold px-5 py-2 text-xs font-semibold text-white hover:scale-105 transition-transform">Save Preferences</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PrefRow = ({ icon: Icon, title, desc, checked, onChange, locked, testid }) => (
  <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-4">
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-5 w-5 text-gold" strokeWidth={1.5} />
      <div>
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-xs text-zinc-400">{desc}</p>
      </div>
    </div>
    <Switch data-testid={testid} checked={checked} disabled={locked} onCheckedChange={onChange} />
  </div>
);

export default CookieConsent;
